import _ from 'underscore';
import Backbone from 'backbone';

/**
 * Validator for native field based on viewmodel-schema
 * @param {String} name - name of field
 * @param {Object} schemaField - viewmodel field property
 * @param {Object} viewModel - the backbone model
 * @param {Object} errors - error object to store field errors
 */
class FieldValidator {
  constructor(name, schemaField, viewModel, errors) {
    this.name = name;
    this.validators = schemaField.validation;
    this.convertors = schemaField.conversion;
    this.errors = errors;

    viewModel.on(`change:${name}`, (vm, value) => {
      this.validate(value);
    });
  }

  /**
 * validate field value based on viewmodel-schema.
 * @param {Object} value - field value
 * @returns {undefined}
 */
  validate(value) {
    this.errors.unset(this.name);

    const modelValue = this.convertors.toModel(value);
    const failedValidators = _.pick(this.validators, v => !v.isValid(modelValue));

    if (!_.isEmpty(failedValidators)) {
      this.errors.set(this.name, this.errorMessages(failedValidators, value));
    }
  }

  /**
 * Get error messages from validators for value
 * @param {Validator[]} validators - array of validator
 * @param {any} value - the value to be validated with validators
 * @returns {string[]} - list error message of failed validators
 */
  errorMessages(validators, value) {
    let errors = [];

    _.each(validators, (failed) => {
      const messages = _.isFunction(failed.message) ? failed.message(value) : failed.message;

      if (_.isArray(messages)) {
        errors = errors.concat(messages);
      } else {
        errors.push(messages);
      }
    });

    return errors;
  }
}

/**
 * Validator for nested entity field based on viewmodel-schema
 * @param {String} name - name of field
 * @param {Object} errors - error object to store field errors
 */
class NestedValidator {
  constructor(name, errors) {
    this.name = name;
    this.errors = errors;
  }

  /**
 * validate field value based on viewmodel-schema.
 * @param {Object} value - field value
 * @returns {undefined}
 */
  validate(value) {
    this.errors.unset(this.name);

    if (!_.isEmpty(value.errors.toJSON())) {
      this.errors.set(this.name, value.errors);
    }
  }
}

/**
 * Validator based on viewmodel-schema
 * @param {Object} schemaField - viewmodel field property
 * @param {Object} viewModel - the backbone
 */
export class Validator {
  constructor(vmSchema, viewModel) {
    this.errors = new Backbone.Model();
    this.fieldValidators = _.mapObject(vmSchema, (field, name) => (field.prop.type === 'object' ?
      new NestedValidator(name, this.errors) :
      new FieldValidator(name, field, viewModel, this.errors)));
  }

  /**
 * validate field attributes for backbone model.
 * @param {Array} fields - array of field attributes
 * @returns {Object} - error object if any, undefined if validation is successful
 */
  validate(fields) {
    _.each(fields, (value, name) => {
      if (_.has(this.fieldValidators, name)) {
        this.fieldValidators[name].validate(value);
      }
    });

    const result = this.errors.toJSON();

    return _.isEmpty(result) ? null : result;
  }
}
